import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, describeError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      // Sign straight in with the new password; the sign-in page is the fallback.
      try {
        await login(data.email, password, true);
        toast.success("Password updated. You're signed in.");
        navigate("/lobby", { replace: true });
      } catch {
        toast.success("Password updated. Sign in with your new password.");
        navigate("/login", { replace: true });
      }
    } catch (err) {
      const msg = describeError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl font-black tracking-tighter">Choose a new password</h1>
        </div>

        {!token ? (
          <div className="bg-surface border border-border rounded-2xl p-8 space-y-5 text-sm">
            <p>This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="text-foreground underline underline-offset-4">
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-surface border border-border rounded-2xl p-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                New password
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-background h-11"
                data-testid="reset-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Confirm password
              </Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="bg-background h-11"
                data-testid="reset-password-confirm-input"
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2" data-testid="reset-password-error">
                {error}{" "}
                {/expired|invalid/i.test(error) && (
                  <Link to="/forgot-password" className="underline underline-offset-4">Request a new link</Link>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-brand-foreground font-semibold"
              data-testid="reset-password-submit-button"
            >
              {loading ? "Saving…" : "Set new password"}
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
